import fetchOffers, { type ListOffer } from '~/lib/goliath/offers';
import { getPrice } from '@i22/rocket';
import type { Attachment, ProductAlternativeItemGroup } from '~/lib/goliath/goliath.types';

/**
 * This function creates from multiple article numbers a "bundle", like the BRODOS-Bundle from Goliath.
 * All components etc. will handle this bundle link a regular BRODOS-Bundle.
 * In a few parts, we have to resolve the article number, which is done by using "isManualBundle".
 * The important part of the `add-to-cart-ts` is, to split the custom articleNumber and add all products, instead of one.
 */

export const getBundleArticleNumbers = (offer: ListOffer): string[] =>
  (offer?.product?.articleNumber || '').replace('manual-bundle-', '')?.split(',');

export const isManualBundle = (articleNumber: string | undefined | null): boolean =>
  articleNumber?.startsWith('manual-bundle-') || false;

export const getBundle = async (
  articleNumbers: string[],
  name: string,
  slug: string,
  allowOnlyAllProducts = true
) => {
  // get all offers as list
  const offers = await fetchOffers({
    filter: {
      articleNumbers,
    },
    includeProductOfferData: true,
  });

  if (!offers?.entries) return undefined;

  const offerEntries = offers?.entries
    // check if the Product has Stock.
    ?.map((offer) => ((offer?.product?.availability?.stock || 0) > 0 ? offer : undefined))
    .filter(Boolean);

  // Sort offerEntries by the order of articleNumbers
  offerEntries.sort((a, b) => {
    const articleNumberA = a.product?.articleNumber;
    const articleNumberB = b.product?.articleNumber;

    if (articleNumberA && articleNumberB) {
      return articleNumbers.indexOf(articleNumberA) - articleNumbers.indexOf(articleNumberB);
    }
    return 0; // If one of the article numbers is missing, keep original order (optional)
  });

  if (
    !offerEntries ||
    offerEntries.length === 0 ||
    // Check if all products are available.
    (allowOnlyAllProducts && offerEntries.length !== articleNumbers.length)
  ) {
    return undefined;
  }

  // extract each product as bundleProduct
  const bundledProducts = offerEntries.map((entry) => ({
    ...entry?.product,
    media: entry?.media || [], // Bundles have the Media on the product, not on the offer!
  }));

  // Map alternatives as productAlternatives to show the "offerConfigurator"
  const productAlternatives: ProductAlternativeItemGroup[] = [];
  // Search all products alternatives. We do not wand different colors.
  offerEntries.forEach((entry) => {
    if (!entry?.productAlternatives) return;
    entry.productAlternatives.forEach((alternativeGroup) => {
      alternativeGroup?.properties?.forEach((property) => {
        if (!property?.alternatives) return;
        property.alternatives.forEach((alternative) => {
          if (!articleNumbers.includes(alternative?.offer?.product?.articleNumber || '')) return;
          productAlternatives.push({
            ...alternativeGroup,
            position: productAlternatives.length,
            properties: [
              {
                ...property,
                alternatives: [alternative],
              },
            ],
          });
        });
      });
    });
  });

  // collect images and sum prices and get the minimum stock.
  const media: Attachment[] = [];
  let priceWas = 0;
  let priceDefault = 0;
  let stock: number | undefined;
  offerEntries.forEach((o) => {
    if (!o) return;
    priceDefault += getPrice(o.prices || [], 'DEFAULT', 'ONETIME')?.amount || 0;
    priceWas += getPrice(o.prices || [], 'WAS', 'ONETIME')?.amount || 0;
    if (o.media?.[0]) {
      media.push(o.media[0]);
    }
    if (stock === undefined) {
      stock = o.product?.availability?.stock || 0;
    }
    stock = Math.min(stock, o.product?.availability?.stock || 0);
  });

  return {
    maxOrderableAmount: null,
    media,
    prices: [
      {
        amount: priceDefault,
        currency: 'EUR',
        paymentFrequency: 'ONETIME',
        type: 'DEFAULT',
        value: priceDefault / 100,
      },
      {
        amount: priceWas || 0,
        currency: 'EUR',
        paymentFrequency: 'ONETIME',
        type: 'WAS',
        value: priceWas / 100,
      },
    ],
    product: {
      availability: {
        ...(offerEntries[0]?.product?.availability || {}),
        stock,
      },
      articleNumber: `manual-bundle-${offerEntries
        .map((o) => o?.product?.articleNumber)
        .join(',')}`,
      bundle: true,
      bundledProducts,
      description: null,
      family: {
        // @ts-ignore
        ...(bundledProducts[0]?.family || {}),
        name,
        primaryCategory: {
          path: '',
        },
      },
      legalTexts: [],
      name,
      origin: 'TELEKOM',
      slug,
      tags: [],
      usps: [],
    },
    productAlternatives,
    teaserImage: media[0],
  };
};
